"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getBraintreeClientToken } from "@/networking/fast-checkout/braintree.api";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BraintreePaypalCheckout = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BraintreeVenmo = any;

interface BraintreeState {
  isReady: boolean;
  shouldShowPayPal: boolean;
  shouldShowVenmo: boolean;
  paypalCheckoutInstance: BraintreePaypalCheckout | null;
  venmoInstance: BraintreeVenmo | null;
  deviceData: string | null;
  error: string | null;
}

interface BraintreeContextType extends BraintreeState {
  braintreePaymentAccountId: string;
}

const INITIAL_STATE: BraintreeState = {
  isReady: false,
  shouldShowPayPal: false,
  shouldShowVenmo: false,
  paypalCheckoutInstance: null,
  venmoInstance: null,
  deviceData: null,
  error: null,
};

const BraintreeContext = createContext<BraintreeContextType>({
  ...INITIAL_STATE,
  braintreePaymentAccountId: "",
});

export function useBraintree() {
  return useContext(BraintreeContext);
}

interface BraintreeProviderProps {
  children: ReactNode;
  cartToken: string;
  paymentAccountId: string;
  currencyCode: string;
}

export function BraintreeProvider({
  children,
  cartToken,
  paymentAccountId,
  currencyCode,
}: BraintreeProviderProps) {
  const [state, setState] = useState<BraintreeState>(INITIAL_STATE);

  useEffect(() => {
    if (!cartToken || !paymentAccountId) return;

    let cancelled = false;
    let paypalInst: BraintreePaypalCheckout | null = null;
    let venmoInst: BraintreeVenmo | null = null;
    setState(INITIAL_STATE);

    async function initialize() {
      try {
        const { client_token, paypal_enabled } = await getBraintreeClientToken(
          cartToken,
          paymentAccountId,
        );
        if (cancelled) return;

        const [braintreeClient, braintreeVenmo, braintreeDataCollector] =
          await Promise.all([
            import("braintree-web/client"),
            import("braintree-web/venmo"),
            import("braintree-web/data-collector"),
          ]);
        const braintreePaypalCheckout = paypal_enabled
          ? await import("braintree-web/paypal-checkout")
          : null;
        if (cancelled) return;

        const clientInstance = await braintreeClient.create({
          authorization: client_token,
        });
        if (cancelled) return;

        if (paypal_enabled && braintreePaypalCheckout) {
          paypalInst = await braintreePaypalCheckout.create({
            client: clientInstance,
          });
        }

        let showVenmo = false;
        try {
          console.info("[Braintree Venmo] Creating Venmo instance...");
          venmoInst = await braintreeVenmo.create({
            client: clientInstance,
            allowDesktop: true,
            paymentMethodUsage: "multi_use",
            collectCustomerBillingAddress: true,
            collectCustomerShippingAddress: true,
          });
          showVenmo = venmoInst.isBrowserSupported();
          console.info(
            "[Braintree Venmo] Instance created. Browser supported:",
            showVenmo,
          );
        } catch (venmoErr) {
          console.info(
            "[Braintree Venmo] Not available in this browser:",
            venmoErr,
          );
        }

        let deviceDataStr: string | null = null;
        try {
          const dataCollectorInstance = await braintreeDataCollector.create({
            client: clientInstance,
          });
          deviceDataStr = dataCollectorInstance.deviceData;
        } catch {
          console.warn("Braintree DataCollector failed to initialize");
        }

        if (cancelled) return;

        console.info(
          "[Braintree] SDK ready — PayPal:",
          paypal_enabled,
          "Venmo:",
          showVenmo,
          "DeviceData:",
          !!deviceDataStr,
        );
        setState({
          isReady: true,
          shouldShowPayPal: paypal_enabled,
          shouldShowVenmo: showVenmo,
          paypalCheckoutInstance: paypalInst,
          venmoInstance: showVenmo ? venmoInst : null,
          deviceData: deviceDataStr,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to initialize Braintree SDK:", err);
        setState({
          ...INITIAL_STATE,
          error:
            err instanceof Error
              ? err.message
              : "Braintree initialization failed",
        });
      }
    }

    initialize();

    return () => {
      cancelled = true;
      paypalInst?.teardown().catch(() => {});
      venmoInst?.teardown().catch(() => {});
    };
  }, [cartToken, paymentAccountId, currencyCode]);

  return (
    <BraintreeContext.Provider
      value={{
        ...state,
        braintreePaymentAccountId: paymentAccountId,
      }}
    >
      {children}
    </BraintreeContext.Provider>
  );
}
